<template>
    <MultiSelect
        v-model="state.selection"
        :options="state.expertisesAvailable"
        option-label="label"
        filter
        :filter-placeholder="t('profile.creation.completion.wizard.industry.expertise.filter.placeholder')"
        class="joy-p-multiselect"
        :show-toggle-all="false"
        display="chip"
        :checkbox-icon="noIcon"
        scroll-height="35vh"
        :placeholder="placeholder"
        append-to="self"
        data-testid="industry-expertise-multiselect"
        @change="handleSelectionChanged"
        @show="handleDropDownOpened"
        @hide="handleDropDownClosed"
    >
        <template #dropdownicon>
            <VJoyIcon :class="joyIconClassPanelOpened" class="p-multiselect-chevron" name="chevron-down" size="xsmall"></VJoyIcon>
        </template>
        <template #filtericon>
            <div></div>
        </template>
        <template #value="modelValue">
            <div v-if="modelValue.value && modelValue.value.length > 0" class="industry-expertise-tags">
                <VJoyTag v-for="option of modelValue.value" :key="option" :label="option.label" removable @remove="handleOptionRemoved(option)" />
            </div>

            <template v-if="!modelValue.value || modelValue.value.length === 0">{{ placeholder }}</template>
        </template>
    </MultiSelect>
</template>

<script setup lang="ts">
    import type {Ref} from 'vue';
    import {computed, reactive, ref, toRef, watch} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyIcon, VJoyTag} from '@maltjoy/core-vue';
    import type {IndustryExpertiseItem} from '~/types/domain/skill/Industry';

    const {t} = useTranslation();

    const props = defineProps<{
        allExpertises: IndustryExpertiseItem[];
        selectedExpertises: IndustryExpertiseItem[];
        maxSelection: number;
    }>();

    type IndustryExpertiseEvents = {
        (e: 'industry-expertise:updated', expertises: IndustryExpertiseItem[]): void;
        (e: 'industry-expertise:max-selection-reached'): void;
    };
    const emit = defineEmits<IndustryExpertiseEvents>();

    const placeholder = t('profile.creation.completion.wizard.industry.expertise.placeholder');

    const state = reactive<{selection: IndustryExpertiseItem[]; expertisesAvailable: Ref<IndustryExpertiseItem[]>; max: number}>({
        selection: props.selectedExpertises.map((expertise) => {
            return {id: expertise.id, label: expertise.label};
        }),
        expertisesAvailable: toRef(props, 'allExpertises'),
        max: props.maxSelection,
    });

    watch(
        () => props.selectedExpertises,
        (selection) => {
            state.selection = selection;
        },
    );

    const noIcon = '';

    const joyIconClassPanelOpened = ref<string>('');

    function handleOptionRemoved(optionSelected: {id: string; label: string}) {
        state.selection = state.selection.filter((option) => option.label !== optionSelected.label);
        emit('industry-expertise:updated', state.selection);
    }

    function handleSelectionChanged(): void {
        if (state.selection.length > state.max) {
            state.selection.pop();
            emit('industry-expertise:max-selection-reached');
            return;
        }
        emit('industry-expertise:updated', state.selection);
    }

    function handleDropDownOpened() {
        joyIconClassPanelOpened.value = 'p-multiselect-chevron-top';
    }

    function handleDropDownClosed() {
        joyIconClassPanelOpened.value = '';
    }
</script>

<style lang="scss" scoped>
    .industry-expertise-tags {
        display: contents;
    }

    :deep(.p-multiselect-filter-icon) {
        display: none;
    }

    :deep(.p-multiselect-close) {
        display: none;
    }
</style>
